import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Input } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { ContractFormValues } from '../../contract-form';
import { TYPE_TO_WAREHOUSE_TYPES, capacitySpec, modelsForCategories, useVbmModels } from './warehouse-models';
import { NumericInput } from '@/components/numeric-input';

/**
 * Row for `type = 'server-warehouse'` — the main server config option.
 * Fields: model (drives brand + capacity auto-fill), brand (read-only), service_tag, capacity_value, capacity_unit.
 * Model catalog is queried LIVE from the warehouse service (see warehouse-models).
 */
export function ServerVbmServerRow({
  optsPath,
  rowIndex,
}: {
  optsPath: string;
  rowIndex: number;
}) {
  const { control, setValue, getValues } = useFormContext<ContractFormValues>();
  const rowPath = `${optsPath}.${rowIndex}` as never;
  const nameValue = (useWatch({ control, name: `${rowPath}.name` as never }) as string) ?? '';

  const { models, manufactureOptions } = useVbmModels();

  const modelOptions = useMemo(() => {
    const allowedTypes = TYPE_TO_WAREHOUSE_TYPES[nameValue] ?? [];
    return modelsForCategories(models, allowedTypes).map((m) => ({ label: m.name, value: m.name }));
  }, [models, nameValue]);

  function handleModelChange(value: string) {
    if (!value) {
      setValue(`${rowPath}.capacity_value` as never, null as never, { shouldDirty: true });
      setValue(`${rowPath}.brand_name` as never, null as never, { shouldDirty: true });
      setValue(`${rowPath}.capacity_unit` as never, null as never, { shouldDirty: true });
      return;
    }
    const model = models.find((m) => m.name === value);
    // Server-warehouse always exposes CPU cores.
    const cpu = capacitySpec(model, 'CPU');
    setValue(`${rowPath}.capacity_value` as never, cpu.value as never, { shouldDirty: true });
    setValue(`${rowPath}.brand_name` as never, (model?.brand ?? getValues(`${rowPath}.brand_name` as never)) as never, { shouldDirty: true });
    setValue(`${rowPath}.capacity_unit` as never, 'core' as never, { shouldDirty: true });
  }

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gap: 'var(--vn-space-3)',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-3)' }}>
      <div style={gridStyle}>
        {/* Model — drives brand + capacity auto-fill */}
        <Controller
          control={control}
          name={`${rowPath}.model_name` as never}
          render={({ field }) => (
            <Field label="Model">
              {(p) => (
                <Select
                  value={(field.value as string) || ''}
                  onValueChange={(v) => { field.onChange(v); handleModelChange(v); }}
                >
                  <SelectTrigger {...p} onBlur={field.onBlur}>
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {modelOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          )}
        />
        {/* Brand — read-only, auto-filled from model selection */}
        <Controller
          control={control}
          name={`${rowPath}.brand_name` as never}
          render={({ field }) => (
            <Field label="Manufacture">
              {(p) => (
                <Select value={(field.value as string) || ''} onValueChange={field.onChange} disabled>
                  <SelectTrigger {...p} onBlur={field.onBlur}>
                    <SelectValue placeholder="Auto" />
                  </SelectTrigger>
                  <SelectContent>
                    {manufactureOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          )}
        />
        {/* Service tag — free text identifier for the physical unit */}
        <Controller
          control={control}
          name={`${rowPath}.service_tag` as never}
          render={({ field }) => (
            <Field label="Service tag">
              {(p) => (
                <Input
                  {...p}
                  type="text"
                  placeholder="Service tag"
                  value={(field.value as string) ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              )}
            </Field>
          )}
        />
        {/* vCores — auto-filled from model specs, editable override */}
        <Controller
          control={control}
          name={`${rowPath}.capacity_value` as never}
          render={({ field }) => (
            <Field label="vCores">
              {(p) => (
                <NumericInput
                  {...p}
                  placeholder="vCores"
                  value={field.value == null ? '' : String(field.value)}
                  onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              )}
            </Field>
          )}
        />
        {/* Capacity unit — fixed to 'core' for server-warehouse, read-only */}
        <Controller
          control={control}
          name={`${rowPath}.capacity_unit` as never}
          render={({ field }) => (
            <Field label="Unit">
              {(p) => (
                <Input
                  {...p}
                  type="text"
                  disabled
                  value={(field.value as string) ?? 'core'}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              )}
            </Field>
          )}
        />
      </div>
    </div>
  );
}
